import { useState } from 'react';

import { FormikHelpers } from 'formik';
import { toast } from 'sonner';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

import { DailyTaskCards } from './dailyTaskCards';
import { DailyTaskEditModal } from './dailyTaskEditModal/DailyTaskEditModal';
import { DailyTaskForm } from './dailyTaskForm/DailyTaskForm';
import { DAILY_TASK_FORM_INITIAL_VALUES, DailyTaskFormValues } from './dailyTaskForm/form';
import { buildDailyTaskRequest, getRewardsPayload } from './dailyTaskForm/utils';

import css from './DailyTask.module.css';

import { rewardsService } from 'v2/api/rewardsService';
import { tasksService } from 'v2/api/tasksService';
import { Card } from 'v2/components/card/Card';
import { SpecialDropsSection } from 'v2/pages/spinBanner/specialDrops';
import { SpecialDropType } from 'v2/types/SpecialDropModel';
import { handleHttpError } from 'v2/utils';

export const DailyTask = () => {
  const [currentDailyTaskId, setCurrentDailyTaskId] = useState<string>('');
  const [isEditModalOpened, setIsEditModalOpened] = useState(false);

  const { data: dailyTasks = [], isLoading, mutate } = useSWR('getAllDailyTasks', tasksService.getAllDailyTasks);

  const { trigger: triggerCreateRewards, isMutating: isCreatingRewards } = useSWRMutation(
    'createRewards',
    rewardsService.createRewards
  );

  const { trigger: triggerCreateDailyTask, isMutating: isCreatingDailyTask } = useSWRMutation(
    'createDailyTask',
    tasksService.createDailyTask
  );

  const { trigger: triggerDeleteDailyTask } = useSWRMutation('deleteDailyTask', (_key, { arg }: { arg: string }) =>
    tasksService.deleteDailyTask(['deleteDailyTask', arg])
  );

  const handleCreateDailyTask = async (
    formValues: DailyTaskFormValues,
    { resetForm }: FormikHelpers<DailyTaskFormValues>
  ) => {
    try {
      const rewardsPayload = getRewardsPayload(formValues.rewards, formValues.isSpecial);
      const createdRewardIds = await triggerCreateRewards(rewardsPayload);

      const request = buildDailyTaskRequest({
        values: formValues,
        defaultRewardIds: formValues.isSpecial ? [] : createdRewardIds,
        specialRewardIds: formValues.isSpecial ? createdRewardIds : [],
      });

      await triggerCreateDailyTask(request);

      toast.success('Daily task has been successfully created');
      await mutate();
      resetForm();
    } catch (error) {
      await handleHttpError(error);
    }
  };

  const handleDeleteDailyTask = async (dailyTaskId: string) => {
    try {
      await triggerDeleteDailyTask(dailyTaskId);
      toast.success('Daily task has been successfully deleted');
      await mutate();
    } catch (error) {
      await handleHttpError(error);
    }
  };

  const handleEditModalOpen = (dailyTaskId: string) => {
    setCurrentDailyTaskId(dailyTaskId);
    setIsEditModalOpened(true);
  };

  const handleEditModalClose = () => {
    setCurrentDailyTaskId('');
    setIsEditModalOpened(false);
  };

  const currentDailyTask = dailyTasks.find((task) => task.id === currentDailyTaskId) ?? null;
  const isProcessing = isCreatingRewards || isCreatingDailyTask;

  return (
    <div className={css.dailyTask}>
      <Typography fontSize="24px" fontWeight={600}>
        Daily Task
      </Typography>

      <div className={css.dailyTaskLayout}>
        <Card className={css.dailyTaskFormColumn}>
          <Typography fontSize="18px" fontWeight={600}>
            Create Daily Task
          </Typography>

          <DailyTaskForm
            submitButtonCaption="Create"
            initialValues={DAILY_TASK_FORM_INITIAL_VALUES}
            isSubmitting={isProcessing}
            onSubmit={handleCreateDailyTask}
          />
        </Card>

        <Box className={css.dailyTaskListColumn}>
          <DailyTaskCards
            cardsTitle="All Daily Tasks"
            items={dailyTasks}
            openEditModal={handleEditModalOpen}
            openDeleteModal={handleDeleteDailyTask}
            isItemsLoading={isLoading}
          />
        </Box>
      </div>

      <DailyTaskEditModal
        dailyTask={currentDailyTask}
        isOpened={isEditModalOpened}
        handleClose={handleEditModalClose}
        onRevalidate={mutate}
      />

      <SpecialDropsSection type={SpecialDropType.DAILY_TASK} title="Special Drop" />
    </div>
  );
};
